/**
 * Heading Component
 *
 * A heading element (h1-h6) for titles and headings.
 * Component follows Smoothie UI architecture.
 *
 * @module @smoothie-framework/ui/components
 */
import { createNodeId } from '../../render/node-id';
import { buildUtilityClass, hasFlatThemeColorAlias, resolveResponsiveValue } from '@smoothie-framework/ui-styles/styling';
import { BUILT_IN_COMPONENT_VARIANTS } from '@smoothie-framework/ui-styles/styling/contract';
import { buildComponentClassNames, resolveResponsivePropValue, } from '../../authoring/internal';
import { extractStaticText, normalizeChildrenToNodes } from '../../internal/children';
import { mergeStyleStrings } from '../../internal/style';
import { extensionIndex } from '../../authoring/extension-registry';
import { getTheme } from '../../theme/theme_context';
const HEADING_LEVEL_TO_SIZE = {
    1: '4xl',
    2: '3xl',
    3: '2xl',
    4: 'xl',
    5: 'lg',
    6: 'md',
};
function isClassToken(value) {
    return /^[a-z0-9-]+$/i.test(value);
}
const HEADING_COLOR_CLASS_TOKENS = new Set((BUILT_IN_COMPONENT_VARIANTS.Heading.color ?? []).filter((value) => typeof value === 'string'));
function usesHeadingColorClass(value) {
    if (!isClassToken(value)) {
        return false;
    }
    if (HEADING_COLOR_CLASS_TOKENS.has(value)) {
        return true;
    }
    const extensionVariants = extensionIndex.mergeRecipeExtensions('Heading').variants.color ?? {};
    return Object.prototype.hasOwnProperty.call(extensionVariants, value);
}
export function Heading(props) {
    const { id, level = 2, textStyle, size, weight = 'bold', color = 'default', align = 'left', uppercase, style, className, children, 'data-ui': dataUi, } = props;
    const theme = getTheme();
    const baseClass = 'smth-heading';
    const defaultSize = HEADING_LEVEL_TO_SIZE[level];
    const resolvedSize = resolveResponsivePropValue(size, defaultSize);
    const resolvedAlign = resolveResponsivePropValue(align, 'left');
    const resolvedWeight = resolveResponsivePropValue(weight, 'bold');
    const resolvedUppercase = resolveOptionalResponsivePropValue(uppercase) ?? false;
    const usesRecipeColorClass = typeof color === 'string' && usesHeadingColorClass(color);
    const usesThemeColorUtilityClass = typeof color === 'string'
        && !usesRecipeColorClass
        && hasFlatThemeColorAlias(theme, color);
    const classes = [
        buildComponentClassNames(baseClass, { size, align, weight, uppercase }, className, { fallbackVariants: { size: defaultSize, align: 'left', weight: 'bold' } }),
    ];
    if (usesRecipeColorClass) {
        classes.push(`${baseClass}--${color}`);
    }
    else if (usesThemeColorUtilityClass) {
        const utilityClass = buildUtilityClass('color', color);
        if (utilityClass) {
            classes.push(utilityClass);
        }
    }
    const styleParts = [];
    if (typeof color === 'string' && !usesRecipeColorClass && !usesThemeColorUtilityClass) {
        styleParts.push(`color: ${color}`);
    }
    const mergedStyle = mergeStyleStrings(styleParts, style);
    const headingProps = {
        className: classes.join(' '),
        'data-ui': dataUi || 'Heading',
        'data-level': String(level),
        'data-size': resolvedSize ?? String(level),
        ...(resolvedAlign && { 'data-align': resolvedAlign }),
        ...(resolvedWeight && { 'data-weight': resolvedWeight }),
        ...(color && { 'data-color': color }),
        'data-uppercase': resolvedUppercase ? 'true' : 'false',
        ...(textStyle !== undefined && { textStyle }),
        ...(uppercase !== undefined && { uppercase }),
        ...(id && { id }),
        ...(mergedStyle && { style: mergedStyle }),
    };
    const childNodes = normalizeChildrenToNodes(children);
    const textContent = extractStaticText(childNodes);
    const renderedChildren = textContent === undefined
        ? lowerStaticTextChildrenToTextNodes(childNodes)
        : [];
    return {
        id: createNodeId(),
        type: 'Heading',
        props: headingProps,
        children: renderedChildren,
        text: textContent,
        _semanticLevel: level,
    };
}
function lowerStaticTextChildrenToTextNodes(children) {
    return children.map((child) => {
        if (child.type !== 'Text'
            || typeof child.text !== 'string'
            || child.children.length > 0
            || Object.keys(child.props ?? {}).length > 0) {
            return child;
        }
        return {
            ...child,
            type: 'TextNode',
        };
    });
}
function resolveOptionalResponsivePropValue(value) {
    return resolveResponsiveValue(value, undefined, getTheme());
}
export const defaultHeadingProps = {
    level: 2,
    weight: 'bold',
    align: 'left',
    color: 'default',
};
//# sourceMappingURL=Heading.js.map